import getParsedLocalStorageData from "@Components/LeadFormModal/helpers/getParsedLocalStorageData";
import { FORM_KEYS } from "./constant";
import {
  getAvailableStartingPricing,
  getPricingDuration,
} from "@Pages/Search/components/SearchListItem/helper";
import { monthsArray } from "@Components/DatePicker/helper";
import axios from "axios";
import config from "@Config/index";
import currencyData from "constants/currencyConstants";

// eslint-disable-next-line func-names
export const getPaymentCurrencyShort = function (currency: any) {
  return (
    currencyData
      .find(
        (c) =>
          c.short_name.toLowerCase() === currency?.toLowerCase() ||
          c.standard_name.toLowerCase() === currency?.toLowerCase(),
      )
      ?.standard_name.toLowerCase() || "gbp"
  );
};

export const getPaymentDetails = (data: any, selectedConfig: any) => {
  const defaults = {
    deposit: 250,
    currency: data?.pricing?.currency,
  };
  const getFullAmount = (amount: number) => (amount * 1.03626943005).toFixed(2);
  const paymentDetails = {
    pricing: {},
    deposit: "",
    processingFees: 0,
    internationalCharges: "",
    offerDiscount: "",
    amount: 0,
    gateway: "stripe",
    currency: "",
    user_details: {},
  };

  const getCharges = () => {
    switch (data?.meta?.payment_type) {
      case "deposit":
        return (
          selectedConfig?.pricing?.deposit ||
          selectedConfig?.pricing?.max_deposit ||
          defaults.deposit
        );
      case "refundable_token":
        return data?.pricing?.refundable_token;

      case "platform_fee":
        return data?.pricing?.platform_fee;
      default:
        return null;
    }
  };

  const propertyDeposit =
    data?.custom_nationality_payment && Object.keys(data?.custom_nationality_payment)?.length
      ? data.custom_nationality_payment.platform_fee
      : getCharges();

  const depositMultiplier = selectedConfig?.pricing?.deposit_multiplier;
  if (propertyDeposit && propertyDeposit > 0) {
    paymentDetails.pricing = selectedConfig?.pricing;
    paymentDetails.deposit = propertyDeposit;
    paymentDetails.processingFees = parseFloat((propertyDeposit * 0.03626943005).toFixed(2));
    paymentDetails.amount = parseFloat(getFullAmount(propertyDeposit));
    // @ts-ignore
    paymentDetails.deposit_multiplier = depositMultiplier || "NA";
  }

  paymentDetails.currency =
    getPaymentCurrencyShort(
      selectedConfig?.pricing?.original_currency || selectedConfig?.pricing?.currency,
    ) || getPaymentCurrencyShort(defaults.currency);

  return paymentDetails;
};

export function formatDate(
  dateString: { split: (arg0: string) => [any, any, any] },
  isLocalString = false,
) {
  if (!dateString) return "";
  // Split the input date string into parts
  const [day, month, year] = dateString?.split("-");

  // Create a new Date object (month is 0-based, so subtract 1)
  const date = new Date(year, month - 1, day);

  // Format the date to "D MMM, YYYY" format
  const options: any = { day: "numeric", month: "short", year: "numeric" };
  // @ts-ignore
  let formattedDate = isLocalString
    ? new Date(date).toLocaleDateString("en-gb", options)
    : new Intl.DateTimeFormat("en-US", options).format(date);

  formattedDate = formattedDate.replace(/(\d{1,2}) (\w{3}) (\d{4})/, "$1 $2, $3");

  return formattedDate;
}

export function customDateFormatter(startDate: any, durationWithUnit: any) {
  // Parse the start date string into a Date object
  const [restDate, yearString] = startDate.split(", ");
  const [monthName, dayString] = restDate.split(" ");
  const monthIndex = monthsArray.findIndex((m) => m.toLowerCase() === monthName.toLowerCase());
  const date = new Date(`${yearString}-${String(monthIndex + 1).padStart(2, "0")}-${dayString}`);

  // Add the specified duration
  const duration = durationWithUnit[0];
  const unit = durationWithUnit[1];
  switch (unit) {
    case "day":
    case "days":
      date.setDate(date.getDate() + duration);
      break;
    case "month":
    case "months":
      date.setMonth(date.getMonth() + duration);
      break;
    case "year":
    case "years":
      date.setFullYear(date.getFullYear() + duration);
      break;
    case "week":
    case "weeks":
      date.setDate(date.getDate() + duration * 7);
      break;
    default:
      throw new Error("Unsupported duration unit");
  }

  // Format the date to "D MMM, YYYY" format
  const options = { day: "numeric", month: "short", year: "numeric" };
  // @ts-ignore
  const formattedDate = new Intl.DateTimeFormat("en-US", options).format(date);

  return formattedDate;
}

export const savePaymentDetailsToSessionStorage = ({
  lead,
  inventory,
  setAccomodationDetails,
}: any) => {
  const bookingDetails: any = getParsedLocalStorageData(FORM_KEYS.BOOK_FORM);
  const selectedRoom = inventory?.active_children?.find(
    (room: any) => room.id === bookingDetails?.roomType,
  );
  const selectedTennancy = selectedRoom?.active_children?.find(
    (ten: any) => ten.id === bookingDetails?.tenancyId,
  );
  // update tenancy deposit price if tanancy didn't have deposit
  if (selectedTennancy && !selectedTennancy?.pricing?.deposit) {
    selectedTennancy.pricing.deposit =
      selectedRoom?.pricing?.deposit || inventory?.pricing?.deposit;
  }
  const durationWithUnit = (bookingDetails?.custom_duration ?? bookingDetails?.duration)?.split(
    " ",
  );

  const isFlexibleRoom = !selectedTennancy;
  const isFlexible = !(
    selectedTennancy &&
    selectedTennancy?.meta?.available_from_formatted &&
    !bookingDetails?.arrival_date
  );

  const cardSummary = {
    roomName: selectedRoom?.name,
    moveInDate: formatDate(
      bookingDetails?.arrival_date ?? selectedTennancy?.meta?.available_from_formatted,
    ),
    moveOutDate:
      durationWithUnit.length > 0
        ? customDateFormatter(
            bookingDetails?.arrival_date ?? selectedTennancy?.meta?.available_from_formatted,
            durationWithUnit,
          )
        : "",
    price: `${isFlexibleRoom ? "From" : ""} ${getAvailableStartingPricing(
      (isFlexibleRoom ? selectedRoom : selectedTennancy)?.pricing,
    )}/${getPricingDuration(
      (isFlexibleRoom ? selectedRoom : selectedTennancy)?.pricing?.duration,
    )}`,
    duration: bookingDetails?.custom_duration ?? bookingDetails?.duration,
    isPending: isFlexible,
  };
  // organize booking details
  const newBookingDetails: any = {
    user_details: {
      name: bookingDetails["user_details/name"] || "",
      email: bookingDetails["user_details/email"] || "",
      phone: bookingDetails["user_details/phone"] || "",
      phone_code: bookingDetails["user_details/phone_code"] || "",
    },
    // if fixed duration and flexible move in -> bookingDetails?.tenancyId else -> bookingDetails?.inventory_id
    inventory_id: bookingDetails?.tenancyId || bookingDetails?.inventory_id,
    roomType: selectedRoom,
    tenancy: cardSummary?.duration,
    tenancyObj: selectedTennancy?.id
      ? selectedRoom?.active_children?.find((ten: any) => ten.id === selectedTennancy?.id)
      : null,
    moveInDate: cardSummary?.moveInDate,
    moveOutDate: cardSummary?.moveOutDate,
  };
  if (bookingDetails?.arrival_date) newBookingDetails.arrival_date = bookingDetails?.arrival_date;
  if (bookingDetails?.custom_duration)
    newBookingDetails.custom_duration = bookingDetails?.custom_duration;

  sessionStorage.setItem(FORM_KEYS.PAYMENT_BOOKING_DETAILS, JSON.stringify(newBookingDetails));

  // get payment details
  let paymentData: any = getPaymentDetails(
    inventory,
    newBookingDetails?.tenancyObj || newBookingDetails?.roomType,
  );
  if (lead) paymentData.lead_id = lead.id;
  paymentData = {
    ...paymentData,
    inventory_id: newBookingDetails?.inventory_id,
    user_details: newBookingDetails?.user_details,
    name: newBookingDetails?.user_details.name,
    description: `Payment for ${newBookingDetails?.roomType?.name}`,
    additional_details: bookingDetails,
  };

  if (typeof sessionStorage !== "undefined") {
    sessionStorage.setItem(FORM_KEYS.PAYMENT_DETAILS, JSON.stringify(paymentData));
  }
  setAccomodationDetails(cardSummary);
};

export const fetchPayment = async (token: string) => {
  try {
    if (token) {
      const paymentData = await axios(`${config.API_URL}/api/v0/payments/${token}`, {});
      return paymentData?.data?.data;
    }
    return { err: "token not found" };
  } catch (err) {
    return { err: JSON.stringify(err) };
  }
};
export default savePaymentDetailsToSessionStorage;

export const getSecondStepLabel = (paymentType: string) => {
  switch (paymentType) {
    case "deposit":
      return "Deposit Payment";
    case "refundable_token":
      return "Fully Refundable Holding Fee";
    case "platform_fee":
      return "Amber Service Fee";
    case "rent":
      return "Rental Instalment";
    default:
      return "deposit";
  }
};

export const getPathFromURL = (URL: string) => {
  const regex = /^https?:\/\/[^/]+(\/[^?#]*)?/;

  const match = URL?.match(regex);
  return match && match[1] ? match[1] : "/";
};

export const getTotalPaymentValue = (payment_info: any) => {
  if (Array.isArray(payment_info) && payment_info.length > 0) {
    const total = payment_info.find((payment) => payment?.type === "total");

    if (config.IS_CHINA_APP && total) {
      const gatewayCharges =
        payment_info.find((payment) => payment?.type === "gateway_charges")?.amount || 0;
      const amount = parseFloat((total.amount - gatewayCharges).toFixed(2));
      return { ...total, amount };
    }

    return total;
  }

  return {};
};
